// 📄 pages/Admin/components/KnownErrorFormModal.tsx
// Movido desde pages/Ariba/components/ — la creación/edición de errores
// conocidos ahora vive solo en el panel Admin (ver KnownErrorsCatalogSection.tsx);
// la vista pública (KnownErrorsCatalogToolbox.tsx) solo lista y busca.
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Paperclip, Trash2, ExternalLink, Plus } from 'lucide-react';
import {
  createKnownError, updateKnownError, resolveNotaSapUrl, KnownError, KnownErrorFormData,
  KNOWN_ERROR_MODULES, KNOWN_ERROR_SOLUCIONES_POR_MODULO, SISTEMAS_AFECTADOS,
  TIPOS_DOCUMENTO_BN, ESTATUS_DOCUMENTO_BN, KnownErrorPatron, codigosSinTexto
} from '../../../services/knownErrorsService.ts';
import { UploadedAttachment } from '../../../services/storageService.ts';
import { formatFechaVerificacion } from '../../../utils/formatDate.ts';
import { useAuth } from '../../../context/AuthContext.tsx';
import CopyIconButton from '../../../components/shared/CopyIconButton.tsx';

interface KnownErrorFormModalProps {
  defaultModule: string;
  // Presente = modo edición (precarga los campos de este error); ausente
  // = modo creación.
  error?: KnownError | null;
  // Solo en modo creación: valores iniciales (p. ej. "Convertir en error
  // conocido" desde un ticket de la extensión, en SoporteHome.tsx).
  initialData?: KnownErrorPrefill;
  onClose: () => void;
  onSaved: () => void;
}

type FormState = Omit<KnownErrorFormData, 'module' | 'fechaVerificacion'> & { module: string; fechaVerificacionInput: string };
export type KnownErrorPrefill = Partial<Omit<KnownErrorFormData, 'fechaVerificacion'>>;

const emptyForm = (defaultModule: string): FormState => ({
  code: '',
  title: '',
  module: defaultModule,
  solucion: '',
  summary: '',
  cause: '',
  solution: '',
  tags: [],
  sistemaAfectado: '',
  versionAriba: '',
  notaSapUrl: '',
  fechaVerificacionInput: '',
  activo: true,
  tiposDocumento: [],
  estatusAplica: [],
  patrones: []
});

// Firestore devuelve `fechaVerificacion` como Timestamp — convierte a
// "YYYY-MM-DD" para precargar el <input type="date">.
const toDateInputValue = (value: any): string => {
  const date = value?.toDate?.() || (value instanceof Date ? value : null);
  if (!date) return '';
  return date.toISOString().slice(0, 10);
};

export default function KnownErrorFormModal({ defaultModule, error, initialData, onClose, onSaved }: KnownErrorFormModalProps) {
  const { user } = useAuth();
  const editando = !!error;
  const [form, setForm] = useState<FormState>(() =>
    error
      ? {
          code: error.code, title: error.title, module: error.module, solucion: error.solucion || '',
          summary: error.summary, cause: error.cause, solution: error.solution, tags: error.tags,
          sistemaAfectado: error.sistemaAfectado || '', versionAriba: error.versionAriba || '',
          notaSapUrl: error.notaSapUrl || '', fechaVerificacionInput: toDateInputValue(error.fechaVerificacion),
          activo: error.activo !== false,
          tiposDocumento: error.tiposDocumento || [],
          estatusAplica: error.estatusAplica || [],
          patrones: error.patrones || []
        }
      : { ...emptyForm(defaultModule), ...initialData }
  );
  const [tagsInput, setTagsInput] = useState(error ? error.tags.join(', ') : (initialData?.tags || []).join(', '));
  const [existingAttachments, setExistingAttachments] = useState<UploadedAttachment[]>(error?.attachments || []);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error_, setError_] = useState<string | null>(null);
  // Patrones sin texto: se avisa antes de guardar. Hay códigos que por sí solos
  // identifican el error, así que se puede confirmar. Cualquier cambio en los
  // patrones vuelve a pedir la confirmación.
  const [avisoSinTexto, setAvisoSinTexto] = useState(false);
  // El ejemplo de un campo vacío se veía igual que un texto escrito, y un
  // patrón sin texto parecía tenerlo: se marca como ejemplo (gris e itálica).
  const PLACEHOLDER_EJEMPLO = 'placeholder:text-gray-600 placeholder:italic';

  const solucionesDelModulo = KNOWN_ERROR_SOLUCIONES_POR_MODULO[form.module] || [];
  const notaSapPreview = resolveNotaSapUrl(form.notaSapUrl);
  const verificacionPreview = form.fechaVerificacionInput
    ? formatFechaVerificacion(new Date(`${form.fechaVerificacionInput}T00:00:00`))
    : null;

  const handleChange = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Cambiar de módulo invalida la solución/canal elegida (pertenece al
  // submenú del módulo anterior) — se resetea para no guardar una
  // combinación inconsistente.
  const handleModuleChange = (value: string) => {
    setForm(prev => ({ ...prev, module: value, solucion: '' }));
  };

  // Casillas de "Diagnóstico automático": agregan o quitan un valor de la lista.
  const toggleEnLista = (field: 'tiposDocumento' | 'estatusAplica', value: string) => {
    setForm(prev => {
      const lista = prev[field] || [];
      return { ...prev, [field]: lista.includes(value) ? lista.filter(v => v !== value) : [...lista, value] };
    });
  };

  const patrones = form.patrones || [];
  const handlePatronChange = (index: number, campo: keyof KnownErrorPatron, value: string) => {
    setAvisoSinTexto(false);
    setForm(prev => ({
      ...prev,
      patrones: (prev.patrones || []).map((p, i) => (i === index ? { ...p, [campo]: value } : p))
    }));
  };
  const handleAddPatron = () => {
    setAvisoSinTexto(false);
    setForm(prev => ({ ...prev, patrones: [...(prev.patrones || []), { codigo: '', texto: '' }] }));
  };
  const handleRemovePatron = (index: number) => {
    setAvisoSinTexto(false);
    setForm(prev => ({ ...prev, patrones: (prev.patrones || []).filter((_, i) => i !== index) }));
  };

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selected]);
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = (url: string) => {
    setExistingAttachments(prev => prev.filter(a => a.url !== url));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError_(null);

    if (!form.code.trim() || !form.title.trim()) {
      setError_("El código y el título son obligatorios.");
      return;
    }
    if (!user) {
      setError_("Sin sesión activa.");
      return;
    }
    if (form.module === 'ariba' && codigosSinTexto(patrones).length > 0 && !avisoSinTexto) {
      setAvisoSinTexto(true);
      return;
    }

    setSaving(true);
    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
      const { fechaVerificacionInput, ...rest } = form;
      const payload: KnownErrorFormData = {
        ...rest,
        tags,
        fechaVerificacion: fechaVerificacionInput ? new Date(`${fechaVerificacionInput}T00:00:00`) : null
      };
      if (editando && error) {
        await updateKnownError(error.id, payload, existingAttachments, files);
      } else {
        await createKnownError(payload, files, user.uid);
      }
      onSaved();
    } catch (err: any) {
      setError_(err.message || "Error al guardar el error.");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  // Portal a document.body: este modal puede abrirse desde secciones del
  // Admin con sus propios stacking contexts (ver el mismo problema
  // resuelto para la versión pública, antes de moverse acá) — montarlo
  // directo en <body> evita que quede atrapado detrás de algún ancestro
  // con z-index mayor.
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[rgb(var(--c-surface))]">
          <h3 className="text-lg font-medium text-white">{editando ? 'Editar Error Conocido' : 'Nuevo Error Conocido'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error_ && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">
              {error_}
            </div>
          )}

          {/* Fila 1: Código de error* | Sistema afectado */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Código de error <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => handleChange('code', e.target.value)}
                placeholder="ej. AR-401"
                className={`${inputClass} font-mono`}
              />
            </div>
            <div>
              <label className={labelClass}>Sistema afectado</label>
              <select
                value={form.sistemaAfectado}
                onChange={(e) => handleChange('sistemaAfectado', e.target.value)}
                className={inputClass}
              >
                <option value="">Sin especificar</option>
                {SISTEMAS_AFECTADOS.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Clasificación interna existente — se mantiene tal cual, ver
              KNOWN_ERROR_SOLUCIONES_POR_MODULO en knownErrorsService.ts. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Módulo <span className="text-red-400">*</span>
              </label>
              <select
                value={form.module}
                onChange={(e) => handleModuleChange(e.target.value)}
                className={inputClass}
              >
                {KNOWN_ERROR_MODULES.map(m => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>
            {solucionesDelModulo.length > 0 && (
              <div>
                <label className={labelClass}>Solución / Canal</label>
                <select
                  value={form.solucion}
                  onChange={(e) => handleChange('solucion', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Sin especificar</option>
                  {solucionesDelModulo.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Fila 2: Texto breve / título* (ancho completo) */}
          <div>
            <label className={labelClass}>
              Texto breve / título <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="ej. Error 401 Unauthorized en llamadas a la API"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Resumen corto (para el listado)</label>
            <input
              type="text"
              value={form.summary}
              onChange={(e) => handleChange('summary', e.target.value)}
              placeholder="Una línea describiendo la causa"
              className={inputClass}
            />
          </div>

          {/* Fila 3: Causa | Solución paso a paso */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Causa</label>
              <textarea
                value={form.cause}
                onChange={(e) => handleChange('cause', e.target.value)}
                rows={5}
                className={`${inputClass} text-xs placeholder-gray-500 resize-none`}
              />
            </div>
            <div>
              <label className={labelClass}>Solución (paso a paso)</label>
              <textarea
                value={form.solution}
                onChange={(e) => handleChange('solution', e.target.value)}
                rows={5}
                placeholder={"1. ...\n2. ...\n3. ..."}
                className={`${inputClass} text-xs placeholder-gray-500 resize-none font-mono`}
              />
            </div>
          </div>

          {/* Fila 5: Nota SAP | Versión Ariba */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Nota SAP (número o URL)</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={form.notaSapUrl}
                  onChange={(e) => handleChange('notaSapUrl', e.target.value)}
                  placeholder="ej. 3749732"
                  className={inputClass}
                />
                {notaSapPreview && (
                  <>
                    <CopyIconButton value={form.notaSapUrl} title="Copiar número/URL" />
                    <a
                      href={notaSapPreview}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir nota"
                      className="flex-shrink-0 p-2.5 text-gray-400 hover:text-blue-400 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </>
                )}
              </div>
            </div>
            <div>
              <label className={labelClass}>Versión Ariba</label>
              <input
                type="text"
                value={form.versionAriba}
                onChange={(e) => handleChange('versionAriba', e.target.value)}
                placeholder="ej. 2402 FP1"
                className={inputClass}
              />
            </div>
          </div>

          {/* Fila 6: Fecha de verificación | Estado */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Fecha de verificación</label>
              <input
                type="date"
                value={form.fechaVerificacionInput}
                onChange={(e) => handleChange('fechaVerificacionInput', e.target.value)}
                className={inputClass}
              />
              {verificacionPreview && (
                <p className={`text-[11px] mt-1 ${verificacionPreview.vencida ? 'text-amber-400' : 'text-gray-500'}`}>
                  Verificado el {verificacionPreview.texto}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>Estado</label>
              <select
                value={form.activo ? 'activo' : 'inactivo'}
                onChange={(e) => handleChange('activo', e.target.value === 'activo')}
                className={inputClass}
              >
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
          </div>

          {/* Fila 7: Tags */}
          <div>
            <label className={labelClass}>Tags (separados por coma)</label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="oauth, 401, token"
              className={inputClass}
            />
          </div>

          {/* Diagnóstico automático de la extensión de Chrome (solo SAP
              Ariba): ver KnownErrorPatron en knownErrorsService.ts. */}
          {form.module === 'ariba' && (
            <div className="border border-white/10 rounded-lg p-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-white">Diagnóstico automático (extensión de Chrome)</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Cuando un documento de Business Network tenga en su historial un evento que cumpla un patrón (o el mensaje de error de una transacción de CIG, con códigos como CIG-PLT-04643), la extensión mostrará este error con su causa y solución.
                  Dentro de un patrón, el código y el texto deben estar en el mismo evento. Sin patrones, el error no se diagnostica solo.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className={labelClass}>Tipo de documento</p>
                  <div className="flex flex-wrap gap-3">
                    {TIPOS_DOCUMENTO_BN.map(t => (
                      <label key={t.id} className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(form.tiposDocumento || []).includes(t.id)}
                          onChange={() => toggleEnLista('tiposDocumento', t.id)}
                        />
                        {t.label}
                      </label>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">Sin marcar = cualquier documento.</p>
                </div>
                <div>
                  <p className={labelClass}>Estatus del documento</p>
                  <div className="flex flex-wrap gap-3">
                    {ESTATUS_DOCUMENTO_BN.map(e => (
                      <label key={e.id} className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(form.estatusAplica || []).includes(e.id)}
                          onChange={() => toggleEnLista('estatusAplica', e.id)}
                        />
                        {e.label}
                      </label>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">Sin marcar = cualquier estatus.</p>
                </div>
              </div>

              <div>
                <p className={labelClass}>Patrones del historial</p>
                {/* Grilla de 3 columnas: código angosto, texto con el resto
                    del ancho y el botón de quitar. */}
                {patrones.length > 0 && (
                  <div className="grid grid-cols-[10rem_minmax(0,1fr)_2rem] gap-2 mb-1 text-[11px] text-gray-500">
                    <span>Código del evento</span>
                    <span>El texto del evento contiene</span>
                    <span />
                  </div>
                )}
                <div className="space-y-2">
                  {patrones.map((p, idx) => (
                    <div key={idx} className="grid grid-cols-[10rem_minmax(0,1fr)_2rem] gap-2 items-center">
                      <input
                        type="text"
                        value={p.codigo}
                        onChange={(e) => handlePatronChange(idx, 'codigo', e.target.value)}
                        placeholder="Ej.: DOC-197"
                        className={`${inputClass} font-mono ${PLACEHOLDER_EJEMPLO}`}
                      />
                      <input
                        type="text"
                        value={p.texto}
                        onChange={(e) => handlePatronChange(idx, 'texto', e.target.value)}
                        placeholder="Ej.: not found in Partner Directory"
                        className={`${p.codigo.trim() && !p.texto.trim() ? inputClass.replace('border-white/10', 'border-amber-500/60') : inputClass} ${PLACEHOLDER_EJEMPLO}`}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePatron(idx)}
                        title="Quitar patrón"
                        className="text-gray-500 hover:text-red-400 flex-shrink-0 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleAddPatron}
                  className="mt-2 flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Agregar patrón
                </button>
              </div>
            </div>
          )}

          <div>
            <label className={labelClass}>Adjuntos (imágenes o documentos)</label>
            <label className="flex items-center gap-2 justify-center border border-dashed border-white/20 rounded-lg p-3 text-xs text-gray-400 hover:border-blue-400 hover:text-blue-400 cursor-pointer transition-all">
              <Paperclip className="w-4 h-4" />
              Seleccionar archivos
              <input type="file" multiple onChange={handleAddFiles} className="hidden" />
            </label>

            {existingAttachments.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {existingAttachments.map((a) => (
                  <div key={a.url} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300">
                    <span className="truncate">{a.name}</span>
                    <button type="button" onClick={() => handleRemoveExistingAttachment(a.url)} className="text-gray-500 hover:text-red-400 flex-shrink-0 ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {files.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {files.map((f, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300">
                    <span className="truncate">{f.name}</span>
                    <button type="button" onClick={() => handleRemoveFile(idx)} className="text-gray-500 hover:text-red-400 flex-shrink-0 ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {avisoSinTexto && (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 p-3 rounded-lg text-xs space-y-1" role="alert">
              <p className="font-medium">
                {codigosSinTexto(patrones).length === 1 ? 'Un patrón no tiene' : 'Hay patrones sin'} texto: {codigosSinTexto(patrones).join(', ')}
              </p>
              <p>
                Coincidirá con cualquier evento de ese código, aunque la causa sea otra, y el cliente podría ver una solución equivocada. Agrega el texto que distingue este error, o pulsa "Guardar de todos modos" si el código por sí solo lo identifica.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : avisoSinTexto ? 'Guardar de todos modos' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
